import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/TabsBranding";
import { LoginPage } from "./LoginPage";
import { HeaderBranding } from "@/components/layout/HeaderBranding";
import { AnalyticsDashboard } from "@/features/analytics/AnalyticsDashboard";
import { AnalyticsFiltersProvider } from "@/features/analytics/filters-context";
import { Skeleton } from "@/components/ui/Skeleton";

const TabAprendices = lazy(() => import("@/pages/clienteFinal/TabAprendices"));

const TabProgramas = lazy(() => import("@/pages/clienteFinal/TabProgramas"));

export default function ClienteDashboard() {
  const { user, usuarioInfo, loadingUsuarioInfo } = useCurrentUser();
  const [activeTab, setActiveTab] = useState("analytics");

  const clienteFinalId = usuarioInfo?.clienteFinal?.id ?? null;
  const clienteIntermedioId = usuarioInfo?.clienteFinal?.idUsuarioAlta ?? null;

  const lockedFilters = useMemo(() => {
    if (!clienteFinalId) return null;
    const base = { clienteFinalId } as {
      clienteFinalId: number;
      clienteIntermedioId?: number;
    };
    if (clienteIntermedioId) {
      base.clienteIntermedioId = clienteIntermedioId;
    }
    return base;
  }, [clienteFinalId, clienteIntermedioId]);

  useEffect(() => {
    if (usuarioInfo?.clienteFinal?.tipografiaMarca) {
      document.body.style.setProperty(
        "--brand-font-family",
        usuarioInfo.clienteFinal.tipografiaMarca,
      );
    }
  }, [usuarioInfo?.clienteFinal?.tipografiaMarca]);
  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="flex">
      <div className="flex flex-col flex-1 min-h-0">
        <HeaderBranding />
        <main className="flex flex-col items-center gap-4 sm:gap-6 overflow-auto bg-background p-3 sm:p-4 text-foreground flex-1 w-full min-h-0">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
            colorPrimario={usuarioInfo?.clienteFinal?.colorPrimario ?? ""}
            colorSecundario={usuarioInfo?.clienteFinal?.colorSecundario ?? ""}
            tipografiaMarca={usuarioInfo?.clienteFinal?.tipografiaMarca ?? ""}
          >
            <TabsList>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="aprendices">
                Gestión de Aprendices
              </TabsTrigger>
              <TabsTrigger value="programas">Gestión de Programas</TabsTrigger>
            </TabsList>
            <TabsContent value="analytics">
              {activeTab === "analytics" && (
                <>
                  {loadingUsuarioInfo ? (
                    <div className="space-y-3">
                      <Skeleton className="h-8 w-48" />
                      <Skeleton className="h-[600px] w-full" />
                    </div>
                  ) : !lockedFilters ? (
                    <div className="space-y-2">
                      <Skeleton className="h-8 w-48" />
                      <div className="text-sm text-muted-foreground">
                        No encontramos un cliente final asociado a tu cuenta.
                      </div>
                    </div>
                  ) : (
                    <AnalyticsFiltersProvider
                      initialState={lockedFilters}
                      lockedFilters={lockedFilters}
                    >
                      <AnalyticsDashboard />
                    </AnalyticsFiltersProvider>
                  )}
                </>
              )}
            </TabsContent>
            <TabsContent value="aprendices">
              {activeTab === "aprendices" && (
                <Suspense fallback={<div>Cargando Aprendices...</div>}>
                  <TabAprendices />
                </Suspense>
              )}
            </TabsContent>
            <TabsContent value="programas">
              {activeTab === "programas" && (
                <Suspense
                  fallback={<div>Cargando Gestión de Programas...</div>}
                >
                  <TabProgramas />
                </Suspense>
              )}
            </TabsContent>
          </Tabs>
        </main>

        <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50">
          <div className="bg-[var(--background)] border shadow-lg rounded-lg px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 flex flex-col items-end">
            <h1 className="font-bold text-lg sm:text-2xl md:text-3xl bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              RETRO IA
            </h1>
            <p className="text-xs text-foreground font-bold">
              by &lt;ALVESC/&gt;
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
